<template>
  <div :class="['shipment-files-pane', { 'is-drag-over': isDragOver }]" @dragenter.prevent="handleDragEnter"
    @dragover.prevent="handleDragEnter" @dragleave.prevent="handleDragLeave" @drop.prevent="handleDrop">
    <div class="shipment-files-toolbar">
      <button type="button" class="shipment-files-dropzone" :disabled="!tracking || isUploading"
        @click="triggerUploadInput">
        <span class="shipment-files-dropzone-icon">
          <i class="material-icons">attach_file</i>
        </span>
        <span class="shipment-files-dropzone-copy">
          <span class="shipment-files-dropzone-text">Drag and Drop or <span class="shipment-files-dropzone-link">Choose
              File</span></span>
          <small>Supported: images, PDF, XLSX, CSV, TXT. Max 10 MB each.</small>
        </span>
      </button>
      <input ref="uploadInputRef" type="file" class="shipment-files-hidden-input" multiple
        @change="handleUploadInputChange" />
      <input ref="replaceInputRef" type="file" class="shipment-files-hidden-input" @change="handleReplaceInputChange" />
    </div>

    <div v-if="isUploading" class="shipment-files-status-panel">
      <div class="shipment-files-status-copy">
        <span v-if="isUploading">{{ uploadStatusLabel }}</span>
        <span v-else>Drop files to upload</span>
        <small>Supported: images, PDF, XLSX, CSV, TXT. Max 10 MB each.</small>
      </div>
      <div v-if="isUploading" class="shipment-files-progress-shell" aria-live="polite">
        <div class="shipment-files-progress-bar" :style="{ width: `${uploadProgressPercent}%` }"></div>
      </div>
    </div>

    <div v-if="bannerMessage" :class="['shipment-files-banner', `is-${bannerTone}`]">{{ bannerMessage }}</div>

    <div v-if="errorMessage" class="shipment-files-error">{{ errorMessage }}</div>

    <div v-if="isLoading" class="shipment-files-empty">Loading files…</div>
    <div v-else-if="files.length === 0" class="shipment-files-empty">No files attached yet.</div>
    <div v-else class="shipment-files-list scroll-content">
      <div v-for="file in files" :key="file.id" class="shipment-file-row" role="button"
        :tabindex="file.status === 'ready' ? 0 : -1" @click="handleRowOpen(file)"
        @keydown.enter.prevent="handleRowOpen(file)" @keydown.space.prevent="handleRowOpen(file)">
        <div class="shipment-file-main">
          <div :class="['shipment-file-thumbnail', `is-${getPreviewKind(file)}`]">
            <img v-if="thumbnailUrls[file.id]" :src="thumbnailUrls[file.id]" :alt="`${file.displayName} preview`"
              class="shipment-file-thumbnail-image" loading="lazy" />
            <span v-else class="shipment-file-thumbnail-label">{{ getPreviewLabel(file) }}</span>
          </div>
          <div class="shipment-file-copy">
            <button type="button" class="shipment-file-name shipment-file-open-link" :disabled="file.status !== 'ready'"
              :title="file.displayName" @click.stop="handleMenuDownload(file)">
              {{ file.displayName }}
            </button>
            <div class="shipment-file-meta">
              <span>{{ formatType(file) }}</span>
              <span>{{ formatBytes(file.sizeBytes) }}</span>
              <span>v{{ file.version }}</span>
              <span>{{ formatDate(file.updatedAt) }}</span>
            </div>
          </div>
        </div>
        <OverflowMenu :key="`${file.id}-${fileMenuRenderKey}`" class="action-menu-wrapper shipment-file-actions-menu"
          button-label="File actions" :disabled="isUploading" :portal="true" placement="top"
          :trigger-class="['action-menu-trigger', 'action-buttons', 'shipment-file-menu-trigger']"
          :options="getFileMenuOptions(file)" @select="handleFileMenuSelect($event, file)">
          <template #trigger>
            <i class="material-icons">more_horiz</i>
          </template>
        </OverflowMenu>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, ref } from 'vue';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';

import { OverflowMenu, type OverflowMenuOption } from '@/components/ui';
import { db } from '@/modules/firebase';
import { fileClient, type RecordFile } from '@/modules/common/files/FileClient';
import {
  buildRecordFileThumbnailSignature,
  findDuplicateRecordFileUpload,
  getRecordFilePreviewKind,
  getRecordFilePreviewLabel,
  uploadRecordFiles,
  validateLocalRecordFile
} from '@/modules/common/files/RecordFileWorkflow';

import { registerShipmentFilesController, type ShipmentFilesController, type ShipmentFilesOpenContext } from './file-controller';

function timestampToMillis(value: unknown): number {
  if (typeof value === 'number') {
    return value > 1_000_000_000_000 ? value : value * 1000;
  }

  if (value && typeof value === 'object') {
    const maybeTimestamp = value as {
      toMillis?: () => number;
      seconds?: number;
      nanoseconds?: number;
      _seconds?: number;
      _nanoseconds?: number;
    };

    if (typeof maybeTimestamp.toMillis === 'function') {
      return maybeTimestamp.toMillis();
    }

    const seconds = maybeTimestamp.seconds ?? maybeTimestamp._seconds;
    const nanoseconds = maybeTimestamp.nanoseconds ?? maybeTimestamp._nanoseconds ?? 0;
    if (typeof seconds === 'number') {
      return (seconds * 1000) + Math.floor(nanoseconds / 1_000_000);
    }
  }

  return 0;
}

function normalizeSnapshotFile(id: string, value: any): RecordFile {
  return {
    id,
    recordType: value.recordType,
    recordId: value.recordId,
    displayName: value.displayName,
    originalName: value.originalName,
    mimeType: value.mimeType,
    extension: value.extension,
    sizeBytes: Number(value.sizeBytes || 0),
    status: value.status,
    deleted: value.deleted === true,
    createdBy: value.createdBy,
    createdAt: timestampToMillis(value.createdAt),
    updatedAt: timestampToMillis(value.updatedAt),
    storagePath: value.storagePath,
    version: Number(value.version || 1)
  };
}

export default defineComponent({
  name: 'ShipmentFilesPane',
  components: {
    OverflowMenu
  },
  setup() {
    const tracking = ref('');
    const record = ref<any | null>(null);
    const files = ref<RecordFile[]>([]);
    const isLoading = ref(false);
    const isUploading = ref(false);
    const isDragOver = ref(false);
    const bannerMessage = ref('');
    const bannerTone = ref<'info' | 'error'>('info');
    const errorMessage = ref('');
    const uploadProgressPercent = ref(0);
    const uploadStatusLabel = ref('');
    const uploadInputRef = ref<HTMLInputElement | null>(null);
    const replaceInputRef = ref<HTMLInputElement | null>(null);
    const replaceTargetFile = ref<RecordFile | null>(null);
    const thumbnailUrls = ref<Record<string, string>>({});
    const thumbnailSignatures = ref<Record<string, string>>({});
    const fileMenuRenderKey = ref(0);
    let unsubscribe: (() => void) | null = null;
    let bannerTimeoutId: number | null = null;
    const pendingThumbnailIds = new Set<string>();

    const canUpload = computed(() => Boolean(tracking.value) && !isUploading.value);

    function showBanner(message: string, tone: 'info' | 'error' = 'info', durationMs = 5000): void {
      bannerMessage.value = message;
      bannerTone.value = tone;

      if (bannerTimeoutId !== null) {
        window.clearTimeout(bannerTimeoutId);
      }

      bannerTimeoutId = window.setTimeout(() => {
        bannerMessage.value = '';
        bannerTimeoutId = null;
      }, durationMs);
    }

    function syncFilesTabBadge(count: number): void {
      const badge = document.getElementById('shipmentFilesTabCount');
      if (!badge) {
        return;
      }

      if (count > 0) {
        badge.textContent = String(count);
        badge.hidden = false;
        return;
      }

      badge.textContent = '0';
      badge.hidden = true;
    }

    function getPreviewKind(file: RecordFile) {
      return getRecordFilePreviewKind(file);
    }

    function getPreviewLabel(file: RecordFile): string {
      return getRecordFilePreviewLabel(file);
    }

    function buildThumbnailSignature(file: RecordFile): string {
      return buildRecordFileThumbnailSignature(file);
    }

    async function ensureThumbnailUrl(file: RecordFile): Promise<void> {
      const signature = buildThumbnailSignature(file);
      if (thumbnailSignatures.value[file.id] === signature && thumbnailUrls.value[file.id]) {
        return;
      }

      if (pendingThumbnailIds.has(file.id)) {
        return;
      }

      pendingThumbnailIds.add(file.id);

      try {
        const nextUrl = await fileClient.getDownloadUrl(file.storagePath);
        const currentFile = files.value.find(entry => entry.id === file.id);
        if (!currentFile || buildThumbnailSignature(currentFile) !== signature) {
          return;
        }

        thumbnailUrls.value = {
          ...thumbnailUrls.value,
          [file.id]: nextUrl
        };
        thumbnailSignatures.value = {
          ...thumbnailSignatures.value,
          [file.id]: signature
        };
      } catch (error) {
        console.warn('[ShipmentFilesPane] Failed to load file preview:', error);
      } finally {
        pendingThumbnailIds.delete(file.id);
      }
    }

    function syncThumbnailCache(nextFiles: RecordFile[]): void {
      const nextUrls: Record<string, string> = {};
      const nextSignatures: Record<string, string> = {};
      const nextIds = new Set(nextFiles.map(file => file.id));

      for (const file of nextFiles) {
        const signature = buildThumbnailSignature(file);
        if (thumbnailSignatures.value[file.id] === signature && thumbnailUrls.value[file.id]) {
          nextUrls[file.id] = thumbnailUrls.value[file.id];
          nextSignatures[file.id] = signature;
        }
      }

      thumbnailUrls.value = nextUrls;
      thumbnailSignatures.value = nextSignatures;

      for (const pendingId of Array.from(pendingThumbnailIds)) {
        if (!nextIds.has(pendingId)) {
          pendingThumbnailIds.delete(pendingId);
        }
      }

      for (const file of nextFiles) {
        if (getPreviewKind(file) === 'image' && file.status === 'ready') {
          void ensureThumbnailUrl(file);
        }
      }
    }

    function startSubscription(nextTracking: string): void {
      stopSubscription();

      if (!nextTracking) {
        files.value = [];
        return;
      }

      isLoading.value = true;
      unsubscribe = onSnapshot(
        query(collection(db, 'shipments', nextTracking, 'files'), orderBy('createdAt', 'desc')),
        snapshot => {
          const nextFiles = snapshot.docs
            .map(doc => normalizeSnapshotFile(doc.id, doc.data()))
            .filter(file => !file.deleted);
          files.value = nextFiles;
          syncThumbnailCache(nextFiles);
          syncFilesTabBadge(files.value.length);
          isLoading.value = false;
        },
        error => {
          console.warn('[ShipmentFilesPane] Failed to load files:', error);
          errorMessage.value = error instanceof Error ? error.message : 'Failed to load files';
          isLoading.value = false;
        }
      );
    }

    function stopSubscription(): void {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }

      thumbnailUrls.value = {};
      thumbnailSignatures.value = {};
      pendingThumbnailIds.clear();
      syncFilesTabBadge(0);
    }

    function closeFileMenu(): void {
      fileMenuRenderKey.value += 1;
    }

    function triggerUploadInput(): void {
      if (!canUpload.value) {
        return;
      }

      uploadInputRef.value?.click();
    }

    function clearFileInput(input: HTMLInputElement | null): void {
      if (input) {
        input.value = '';
      }
    }

    function validateFile(file: File): string | null {
      return validateLocalRecordFile(file);
    }

    function findDuplicateUpload(fileList: File[]): string | null {
      return findDuplicateRecordFileUpload(fileList, files.value);
    }

    async function uploadFiles(fileList: FileList | File[]): Promise<void> {
      if (!tracking.value || isUploading.value) {
        return;
      }

      errorMessage.value = '';

      const nextFiles = Array.from(fileList);
      for (const file of nextFiles) {
        const validationError = validateFile(file);
        if (validationError) {
          errorMessage.value = validationError;
          return;
        }
      }

      const duplicateFileName = findDuplicateUpload(nextFiles);
      if (duplicateFileName) {
        showBanner(`${duplicateFileName} already exists in this shipment`, 'info');
        clearFileInput(uploadInputRef.value);
        return;
      }

      isUploading.value = true;
      uploadProgressPercent.value = 0;
      uploadStatusLabel.value = `Uploading ${nextFiles.length} file${nextFiles.length === 1 ? '' : 's'}…`;
      closeFileMenu();

      try {
        await uploadRecordFiles({
          recordType: 'shipments',
          recordId: tracking.value,
          files: nextFiles,
          onProgress: progress => {
            uploadProgressPercent.value = progress.percent;
            uploadStatusLabel.value = progress.uploadCount === 1
              ? `Uploading ${progress.fileName} (${progress.percent}%)`
              : `Uploading ${progress.uploadIndex + 1} of ${progress.uploadCount}: ${progress.fileName} (${progress.percent}%)`;
          }
        });

        uploadProgressPercent.value = 100;
        uploadStatusLabel.value = '';
      } catch (error) {
        const nextError = error instanceof Error ? error.message : 'File upload failed';
        if (nextError.toLowerCase().includes('already exists')) {
          showBanner(nextError, 'info');
          errorMessage.value = '';
        } else {
          errorMessage.value = nextError;
        }
      } finally {
        isUploading.value = false;
        clearFileInput(uploadInputRef.value);
      }
    }

    async function renameFile(file: RecordFile): Promise<void> {
      const nextName = window.prompt('Update file name', file.displayName);
      if (!nextName || nextName.trim() === file.displayName) {
        return;
      }

      errorMessage.value = '';
      try {
        await fileClient.updateFile('shipments', tracking.value, file.id, nextName.trim());
      } catch (error) {
        errorMessage.value = error instanceof Error ? error.message : 'Failed to update file';
      }
    }

    function prepareReplace(file: RecordFile): void {
      if (isUploading.value) {
        return;
      }

      replaceTargetFile.value = file;
      replaceInputRef.value?.click();
    }

    async function removeFile(file: RecordFile): Promise<void> {
      if (!window.confirm(`Delete ${file.displayName}?`)) {
        return;
      }

      errorMessage.value = '';
      try {
        await fileClient.deleteFile('shipments', tracking.value, file.id);
      } catch (error) {
        errorMessage.value = error instanceof Error ? error.message : 'Failed to delete file';
      }
    }

    async function downloadFile(file: RecordFile): Promise<void> {
      errorMessage.value = '';
      try {
        const url = await fileClient.getDownloadUrl(file.storagePath);
        window.open(url, '_blank', 'noopener');
      } catch (error) {
        errorMessage.value = error instanceof Error ? error.message : 'Failed to download file';
      }
    }

    function handleUploadInputChange(event: Event): void {
      const input = event.target as HTMLInputElement | null;
      if (input?.files?.length) {
        void uploadFiles(input.files);
      }
    }

    function handleReplaceInputChange(event: Event): void {
      const input = event.target as HTMLInputElement | null;
      const targetFile = replaceTargetFile.value;

      if (!input?.files?.length || !targetFile) {
        clearFileInput(replaceInputRef.value);
        replaceTargetFile.value = null;
        return;
      }

      const file = input.files[0];
      const validationError = validateFile(file);
      if (validationError) {
        errorMessage.value = validationError;
        clearFileInput(replaceInputRef.value);
        replaceTargetFile.value = null;
        return;
      }

      isUploading.value = true;
      errorMessage.value = '';
      uploadProgressPercent.value = 0;
      uploadStatusLabel.value = `Replacing ${targetFile.displayName}…`;
      closeFileMenu();

      void uploadRecordFiles({
        recordType: 'shipments',
        recordId: tracking.value,
        files: [file],
        targetFile,
        onProgress: progress => {
          uploadProgressPercent.value = progress.percent;
          uploadStatusLabel.value = `Replacing ${targetFile.displayName} (${progress.percent}%)`;
        }
      })
        .then(() => {
          uploadProgressPercent.value = 100;
          uploadStatusLabel.value = '';
        })
        .catch(error => {
          errorMessage.value = error instanceof Error ? error.message : 'Failed to replace file';
        })
        .finally(() => {
          isUploading.value = false;
          clearFileInput(replaceInputRef.value);
          replaceTargetFile.value = null;
        });
    }

    function handleDragEnter(): void {
      if (!canUpload.value) {
        return;
      }

      errorMessage.value = '';
      isDragOver.value = true;
    }

    function handleDragLeave(event: DragEvent): void {
      if ((event.currentTarget as HTMLElement | null)?.contains(event.relatedTarget as Node | null)) {
        return;
      }

      isDragOver.value = false;
    }

    function handleDrop(event: DragEvent): void {
      isDragOver.value = false;
      if (event.dataTransfer?.files?.length) {
        void uploadFiles(event.dataTransfer.files);
      }
    }

    function handleMenuDownload(file: RecordFile): void {
      closeFileMenu();
      void downloadFile(file);
    }

    function handleMenuRename(file: RecordFile): void {
      closeFileMenu();
      void renameFile(file);
    }

    function handleMenuReplace(file: RecordFile): void {
      closeFileMenu();
      prepareReplace(file);
    }

    function handleMenuDelete(file: RecordFile): void {
      closeFileMenu();
      void removeFile(file);
    }

    function handleRowOpen(file: RecordFile): void {
      if (file.status !== 'ready' || isUploading.value) {
        return;
      }

      handleMenuDownload(file);
    }

    function getFileMenuOptions(file: RecordFile): OverflowMenuOption[] {
      return [
        {
          key: 'download',
          label: 'Download',
          icon: 'download',
          disabled: file.status !== 'ready'
        },
        {
          key: 'rename',
          label: 'Edit',
          icon: 'edit'
        },
        {
          key: 'replace',
          label: 'Replace',
          icon: 'upload_file'
        },
        {
          key: 'delete',
          label: 'Delete',
          icon: 'delete',
          tone: 'danger'
        }
      ];
    }

    function handleFileMenuSelect(actionKey: string, file: RecordFile): void {
      if (actionKey === 'download') {
        handleMenuDownload(file);
        return;
      }

      if (actionKey === 'rename') {
        handleMenuRename(file);
        return;
      }

      if (actionKey === 'replace') {
        handleMenuReplace(file);
        return;
      }

      if (actionKey === 'delete') {
        handleMenuDelete(file);
      }
    }

    function formatBytes(sizeBytes: number): string {
      if (sizeBytes < 1024) {
        return `${sizeBytes} B`;
      }

      if (sizeBytes < 1024 * 1024) {
        return `${(sizeBytes / 1024).toFixed(1)} KB`;
      }

      return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    function formatDate(timestamp: number): string {
      if (!timestamp) {
        return 'Unknown date';
      }

      return new Date(timestamp).toLocaleString();
    }

    function formatType(file: RecordFile): string {
      return file.extension.toUpperCase();
    }

    function open(context: ShipmentFilesOpenContext): void {
      tracking.value = context.tracking;
      record.value = context.record || null;
      bannerMessage.value = '';
      errorMessage.value = '';
      uploadProgressPercent.value = 0;
      uploadStatusLabel.value = '';
      syncFilesTabBadge(Number(context.record?.fileSummary?.readyCount || 0));
      closeFileMenu();
      startSubscription(context.tracking);
    }

    function updateRecord(nextRecord: any): void {
      record.value = nextRecord || null;
      if (!files.value.length) {
        syncFilesTabBadge(Number(nextRecord?.fileSummary?.readyCount || 0));
      }
    }

    function reset(): void {
      stopSubscription();
      if (bannerTimeoutId !== null) {
        window.clearTimeout(bannerTimeoutId);
        bannerTimeoutId = null;
      }
      pendingThumbnailIds.clear();
      tracking.value = '';
      record.value = null;
      files.value = [];
      isLoading.value = false;
      isUploading.value = false;
      isDragOver.value = false;
      bannerMessage.value = '';
      errorMessage.value = '';
      uploadProgressPercent.value = 0;
      uploadStatusLabel.value = '';
      syncFilesTabBadge(0);
      closeFileMenu();
      replaceTargetFile.value = null;
      clearFileInput(uploadInputRef.value);
      clearFileInput(replaceInputRef.value);
    }

    const controller: ShipmentFilesController = {
      open,
      updateRecord,
      reset
    };

    registerShipmentFilesController(controller);

    onBeforeUnmount(() => {
      stopSubscription();
      if (bannerTimeoutId !== null) {
        window.clearTimeout(bannerTimeoutId);
        bannerTimeoutId = null;
      }
      if (window.ShipmentFilesController === controller) {
        delete window.ShipmentFilesController;
      }
    });

    return {
      downloadFile,
      bannerMessage,
      bannerTone,
      errorMessage,
      files,
      formatBytes,
      formatDate,
      formatType,
      fileMenuRenderKey,
      getFileMenuOptions,
      getPreviewKind,
      getPreviewLabel,
      handleFileMenuSelect,
      handleMenuDelete,
      handleMenuDownload,
      handleMenuRename,
      handleMenuReplace,
      handleRowOpen,
      handleDragEnter,
      handleDragLeave,
      handleDrop,
      handleReplaceInputChange,
      handleUploadInputChange,
      isDragOver,
      isLoading,
      isUploading,
      prepareReplace,
      removeFile,
      renameFile,
      replaceInputRef,
      thumbnailUrls,
      tracking,
      uploadProgressPercent,
      uploadStatusLabel,
      triggerUploadInput,
      uploadInputRef
    };
  }
});
</script>
